import type { SchemaData } from '../schema/types'
import { elementVisibleInMode } from '../schema/types'

/**
 * Night-service membership for the rendered map (TASK-412 M, option (i)).
 *
 * Builds the set of element ids (connections, transitions, stations,
 * additionals) whose `modes` include `'night'` — i.e. the Night Tube
 * network drawn when `currentMode === 'night'`. The renderer applies this
 * as a DRAW-TIME allow-set (no re-tessellation; mirrors iOS N — a
 * visibility filter, not a buffer rebuild): when night is active, an
 * element is drawn only if its id is in this set.
 *
 * Cross-platform parity: the predicate is `elementVisibleInMode(modes,
 * 'night')` — the SAME contract `filterMapDataByMode` uses for the routing
 * graph (iOS `MapData.filtered(by:)`). Elements with `modes === undefined`
 * count as visible-everywhere (legacy/stripped fixtures), so they survive
 * the night filter too — matching the routing-side behaviour.
 *
 * Membership lives in `schema.json` (the MMTG geometry binaries carry no
 * per-element modes), keyed by element id; the renderer's uploaded buffers
 * are keyed by the same ids, so the set intersects cleanly at draw time.
 */
export function nightElementIds(schema: SchemaData): ReadonlySet<string> {
  const ids = new Set<string>()
  const add = (
    list:
      | ReadonlyArray<{ id: string; modes?: readonly import('../schema/types').Mode[] }>
      | undefined,
  ): void => {
    for (const el of list ?? []) {
      if (elementVisibleInMode(el.modes, 'night')) ids.add(el.id)
    }
  }
  add(schema.connections)
  add(schema.transitions)
  add(schema.stations)
  add(schema.additional)
  return ids
}

/**
 * Day draw-time allow-set (TASK-1136). A city with a late-night frame keeps
 * its night-only elements (`modes: ["night"]` — New York's late-night stops,
 * stripes and transfers) in the day MMTG files too, so every day file holds
 * every stop, connection and drawn transfer (api-v2 "Late-night frame"); by
 * day they are hidden through `modes`. Returns the elements visible in
 * normal mode, or `null` when that is every element (London) — no filter,
 * the day map draws as it always did.
 */
export function dayElementIds(schema: SchemaData): ReadonlySet<string> | null {
  const ids = new Set<string>()
  let hidden = false
  const add = (
    list:
      | ReadonlyArray<{ id: string; modes?: readonly import('../schema/types').Mode[] }>
      | undefined,
  ): void => {
    for (const el of list ?? []) {
      if (elementVisibleInMode(el.modes, 'normal')) ids.add(el.id)
      else hidden = true
    }
  }
  add(schema.connections)
  add(schema.transitions)
  add(schema.stations)
  add(schema.additional)
  return hidden ? ids : null
}

/**
 * Night draw-time allow-set for a map that has a SELECTED route highlighted
 * (bug C). Returns `nightElementIds(schema)` UNIONED with the route's
 * highlighted element ids so the selected route survives night mode: legs
 * on non-night lines (Elizabeth / Thameslink / Lioness) are night-filtered
 * out of the base geometry, which would otherwise erase half the route. The
 * union keeps those exact route segments in the drawn set while the rest of
 * their line stays hidden. When nothing is highlighted the base night set is
 * returned unchanged (no route to protect).
 *
 * Pure so the renderer-filter union is unit-testable without mounting the
 * WebGL canvas (jsdom-unreachable). The caller (`MetroMapCanvas`) applies
 * the result via `renderer.setNightVisibleIds` only when night mode is
 * active; day mode passes `null` (draw everything).
 */
export function nightVisibleIdsWithRoute(
  schema: SchemaData,
  highlightedIds: ReadonlySet<string>,
): ReadonlySet<string> {
  const base = nightElementIds(schema)
  if (highlightedIds.size === 0) return base
  const union = new Set(base)
  for (const id of highlightedIds) union.add(id)
  return union
}
